/*
******************************************************************************
Functions related to initialization of individual page classes, including 
updating image paths with NAVS environment directory structure
******************************************************************************
*/

//@ts-nocheck
// Toggle preceding from @ts-nocheck to @ts-check to enable TypeScript checking

import { consoleMsg, selectRadios, updateOfflineStatus, attachRefresherEvent } from "./utility.js";
import { restoreFromLocalStorage, hideDiv, showTextArea, previous } from "./navigation.js";
import { validatePage } from "./validation.js";
import { PhotoManager } from "../classes/photo-manager";
import { damageMgr, inspectionMgr } from "../web-components";

export { initializePage, loadHTML, loadPages, inspectionPages, loadFirstPage, rewriteImgSrcs, mkAbs };

/*
window.addEventListener(
	"orientationchange",
	function () {
		// Announce the new orientation number
		consoleMsg("ORIENTATION: ", window.orientation);
	},
	false
);*/

// Ionic configuration options: toggle mode to 'ios' to force ios styling
window.Ionic = {
	config: {
		rippleEffect: true,
		//mode: "ios",
		//mode: "md",
	},
};

// Function to rewrite paths in page templates with Trevor's prefix
// Prefix non-absolute URLs in src, srcset, ion-img src, and anchor href
function rewriteImgSrcs(html) {
	let regex = /\bsrc=(["'])(?!https?:|data:|blob:|#)([^"']+)\1/gi;
	// _m = matched pattern; q = single or double quote
	html = html.replace(regex, (_m, q, url) => `src=${q}${mkAbs(url)}${q}`);

	regex = /\bsrcset=(["'])([^"']+)\1/gi;
	html = html.replace(regex, (_m, q, list) => {
		const fixed = list
			.split(",")
			.map((token) => {
				const parts = token.trim().split(/\s+/);
				if (!parts[0]) return token;
				parts[0] = mkAbs(parts[0]);
				return parts.join(" ");
			})
			.join(", ");
		return `src=${q}${fixed}${q}`;
	});

	regex = /<ion-img\b([^>]*?)\bsrc=(["'])(?!https?:|data:|blob:|#)([^"']+)\2/gi;
	html = html.replace(regex, (m, _attrs, _q, url) => m.replace(url, mkAbs(url)));

	// Rewrite anchor href for local resources (PDFs, documents, etc.)
	regex = /\bhref=(["'])(?!https?:|mailto:|tel:|data:|blob:|#)([^"']+)\1/gi;
	html = html.replace(regex, (_m, q, url) => `href=${q}${mkAbs(url)}${q}`);

	return html;
}

// Function to convert relative URLs to absolute
function mkAbs(url) {
	// Remove trailing slash
	const base = (app.settings.base || "/").replace(/\/+$/, "");
	if (!url) return url;

	// Already absolute/special? leave it alone
	if (/^(https?:|data:|blob:|capacitor:|#)/i.test(url)) return url;

	// If it's already base-prefixed, do NOT add base again
	if (base && url.startsWith(base + "/")) return url;

	if (url.startsWith("/")) {
		// Root-absolute → honor base subpath once
		return base === "" || base === "/" ? url : `${base}${url}`;
	}

	// Relative → prefix base once
	return `${base}/${url.replace(/^\/+/, "")}`;
}

// Define the common function for initializing page classes
async function initializePage(pageNo, pageElement) {
	// Add guard to force display of Success page if app data already submitted
	if (localStorage.getItem("inspection-submitted")) {
		const router = document.querySelector("ion-router");
		await router.push(app.routes.submissionComplete, "root");
		return;
	}

	sessionStorage.setItem("lastRoute", window.location.hash.slice(1));

	const formId = "form" + pageNo;
	const alertId = "alert" + pageNo;
	const contentId = "content-" + pageNo;
	const pageRadioGroups = document.querySelectorAll("ion-radio-group");

	// Setup initial page state
	await setPageState(pageNo);

	// Development mode: seed a default answer in each group so the user doesn't have to.
	// Must run BEFORE restore, so stored answers overwrite the seed rather than the reverse.
	if (app.settings.checkRadios) {
		selectRadios(pageRadioGroups);
	}

	// Restore any UI values from Ionic Storage
	restoreFromLocalStorage();

	// Set common page elements and check offline status
	performance.mark("start");
	await updateOfflineStatus(contentId);

	/*IMPORTANT!  Need to clear the previous setInterval ID prior to replacing with  a new one to avoid multiple 
   intervals building up and running simultaneously.*/
	clearInterval(app.state.offlineSetIntervalId);

	app.state.offlineSetIntervalId = setInterval(updateOfflineStatus, app.state.offlineCheckInterval, contentId);

	// Attach refresher event listener (to refresh / reload the app)
	attachRefresherEvent(pageElement);

	// Definition of text / photo reset alert MD - DON'T THINK THIS IS NEEDED IN DELIVERY APP
	const photoAlert = document.getElementById(alertId);
	if (photoAlert) {
		configPhotoAlert(photoAlert);
	}

	// Add event listeners for each page radio group
	pageRadioGroups.forEach((radioGroup) => {
		radioGroup.addEventListener("ionChange", showTextArea);
	});
}

// Function for configuring initial page state (called from initializePage)
async function setPageState(pageNo) {
	const pageId = "page" + pageNo;
	const formId = "form" + pageNo;

	app.state.currentPage = pageId;
	if (pageId !== "page1") {
		app.state.backButton = document.getElementById("backButton" + pageNo);
	}
	app.state.nextButton = document.getElementById("nextButton" + pageNo);
	app.state.form = document.getElementById(formId);
	consoleMsg(`Current page = ${app.state.currentPage}`);

	// Remove existing event listeners for back and next buttons
	if (app.state.currentPage !== "page1" && app.state.backButton) {
		app.state.backButton.removeEventListener("click", previous);
	}
	if (app.state.nextButton !== null) {
		app.state.nextButton.removeEventListener("click", validatePage);
	}
}

// Function that configures the text and button behavior of the Exterior Inspection page photo alert
async function configPhotoAlert(photoAlert) {
	photoAlert.buttons = [
		{
			text: "Cancel",
			role: "cancel",
			handler: () => {
				// Define varible for passed alert data
				const alertData = alert.data.textId;
				consoleMsg("Alert data = ", alertData);
				// Derive other constants via string manipulation of passed data
				const radioGroupId = alertData.slice(0, -5) + "-group";
				const radioGroup = document.getElementById(radioGroupId);
				if (radioGroup) {
					// Reset radio group value to 'No' and dispatch ionChange event to update UI
					radioGroup.value = "no";
					radioGroup.dispatchEvent(
						new CustomEvent("ionChange", {
							bubbles: true,
						}),
					);
				}
			},
		},
		{
			text: "OK",
			role: "confirm",
			handler: () => {
				// Define constants for passed alert data
				const textId = photoAlert.data.textId;
				const textArea = photoAlert.data.textArea;
				const divId = photoAlert.data.divId;
				const photos = document.querySelectorAll("#" + divId + " img");
				const photoRow = document.querySelector("#" + divId + " ion-row");
				let photoRowId;
				consoleMsg("photoRow: ", photoRow);
				if (photoRow) {
					photoRowId = photoRow.id;
					consoleMsg("photoRowId: ", photoRowId);
				}
				consoleMsg("Alert confirmed");
				// Reset text area value and hide parent div
				textArea.value = "";
				// If there are any photos in the current row, remove from DOM and storage
				if (photos.length > 0) {
					for (let i = 0; i < photos.length; i++) {
						photos[i].remove();
					}
					store.forEach((value, key) => {
						if (key.includes(photoRowId)) {
							store.remove(key);
						}
					});
				}
				hideDiv(divId);
			},
		},
	];
}

async function loadHTML(url) {
	try {
		const response = await fetch(url);
		return await response.text();
	} catch (error) {
		console.error("Error fetching HTML content:", error);
		return "";
	}
}

/*
IMPORTANT!!  I should document the default app flow for generating HTML content.  I just
performed a fruitless search for 30 minutes trying to track down where content is generated
so I could add the new Mercedes Inspection pages.  I really need to do (at a minimum) flow
diagrams for the primary application work flows.
*/

// Synchronous function definition to address initial page load race condition.
// Update with a more modern approach prior to going live.
function loadFirstPageSync(url, content) {
	const req = new XMLHttpRequest();
	req.open("GET", url, false); // `false` makes it synchronous
	req.send(null);
	if (req.status === 200) {
		app.content[content] = req.responseText;
		consoleMsg("LOAD FIRST PAGE FUNCTION COMPLETED SUCCESSFULLY...");
	} else {
		console.error(`Failed to load ${url}:`, req.status);
	}
}

// Async version of above function: USE THIS INSTEAD
async function loadFirstPage(url, content) {
	const response = await fetch(url);
	if (!response.ok) throw new Error(`Failed to fetch ${url}: ${response.status}`);
	const html = await response.text();
	app.content[content] = html;
	consoleMsg("✅ Loaded first page content into app.content.");
}

async function loadPages(pages) {
	for (const page of pages) {
		// Make sure not to reload the first page
		if (page.content !== "details") {
			const key = page.content.trim();
			// Only store the page if not already stored in app.content
			if (key && app.content[key] === "") {
				let content = await loadHTML(`${app.settings.base}${page.url}`);
				if (key === "photos" || key === "exterior" || key === "mbInterior" || key === "mbRoad") {
					content = rewriteImgSrcs(content);
				}
				app.content[key] = content;
			}
		}
	}
}

window.rewriteImgSrcs = rewriteImgSrcs;

/* IMPORTANT!!  As originally envisoned, app exposed variables for inspection vs. delivery
flows.  This implementation was based on a flawed understanding of NAVS app terminology.  In
NAVS, there is only an inspection, and it has a direction of outgoing vs. incoming.  There is
no concept of a delivery.

The below inspectionPages variable as currently implemented is just a URL to content mapping
container, and is called once from web-components to load all HTML page content and store it in the
global app object.  As the project continues to evolve to support more vendor flows, inspectionPages
should merge into the new page mapping structure (currently only called from the Signature page),
and route info currently stored in the global app object should also move.  As part of this work, a 
new PageManager class will be implemnted, as described in docs/page-flow-refactoring-plan.md.

*/

//prettier-ignore
let inspectionPages = [
   { url: "/pages/details.html", content: "details" }, 
   { url: "/pages/photos.html", content: "photos" }, 
   { url: "/pages/exterior.html", content: "exterior" }, 
   { url: "/pages/glovebox.html", content: "glovebox" }, 
   { url: "/pages/interior.html", content: "interior" }, 
   { url: "/pages/road-test.html", content: "roadTest" }, 
   { url: "/pages/lights.html", content: "lights" }, 
   { url: "/pages/fluids.html", content: "fluids" }, 
   { url: "/pages/tires.html", content: "tires" }, 
   { url: "/pages/signature.html", content: "signature" }, 
   { url: "/pages/app-settings.html", content: "settings" }, 
   { url: "/pages/mb-interior.html", content: "mbInterior" },
   { url: "/pages/mb-road.html", content: "mbRoad" },
   { url: "/pages/mb-communication.html", content: "mbCommunication" },
   { url: "/pages/mb-functions.html", content: "mbFunction" },
   { url: "/pages/mb-out-display.html", content: 'mbOutDisplay' },
   { url: "/pages/mb-out-upon-delivery.html", content: "mbOutUponDelivery" },
   { url: "/pages/mb-out-vehicle-settings.html", content: "mbOutVehicleSettings" },
   { url: "/pages/submission-complete.html", content: "submissionComplete"},
];
